// The `creator-collections-listing` orchestrator — replaces eb-ui
// `src/legacy/app.tsx` + `index.tsx` (`BspAppRoot`).
//
// The PAGE (`src/pages/cc/[collectionId].page.tsx`) already renders the
// sanctioned `BspPageShell` chrome (header/footer) + a `FeatureFlagsProvider`;
// this module provides the per-request server-data context + the full
// collection render tree. (The page's outer `FeatureFlagsProvider` is harmless
// — this module re-provides it INSIDE the app so the ported tree reads it.)
//
// Dropped from the legacy `app.tsx` (documented GA/ghosts — the local
// `_shared` analytics is a no-op): `initializeRealUserMonitoring`,
// `useAttributionClickTracking`/`useTranscendConsent`/clid-courier,
// `displayFXSurveyIntercept`. `AnalyticsContext` still flows the
// {collectionId, organizerId} pair.
import { BspPageShell } from '@eventbrite/valkyrie-app-shell';
import type {
    FooterProps,
    GlobalHeaderProps,
} from '@eventbrite/valkyrie-app-shell';
import React from 'react';

import { AnalyticsContext, ApplicationContext } from './context';
import { languageSubdirectoryFromLocale } from './lib/languageSubdirectory';
import { Collection } from './Collection';
import { CollectionError } from './Collection/CollectionError';
import { OrganizerUiBanner } from './OrganizerUi';
import { PageLayout } from './PageLayout';
import {
    Collection as CollectionType,
    CollectionMovedServerData,
    CollectionServerData,
    MoreCollectionsFromThisOrganizer,
    OtherEventsYouMayLike,
    ServerData,
} from '@/types';
import { AnalyticsCategory } from './_shared';
import {
    FeatureFlagsProvider,
    type FeatureFlags,
} from '@/lib/featureFlags';
import type { ShareProps } from '@/types';
import type { ShareCollectionProps } from '@eventbrite/social-share';
import type { Env } from './types';

/**
 * The `getPropsFrom` transform (legacy `app.tsx`) — localizes the organizer
 * URL via the language sub-directory, prepends the language sub-directory to
 * the `more_collections` relative_urls, and builds
 * `collectionDetailsProps`/`organizerPanelProps`/`collectionShareProps`.
 * PRESERVED verbatim (GA/`gaSettings` are ghosts; `isGDPRCountry` stays `true`
 * as in the legacy source).
 */
export const getPropsFrom = (
    user: { isAuthenticated?: boolean },
    gaSettings: unknown,
    collection: CollectionType,
    share_props: ShareProps,
    languageSubdirectory: string,
    more_collections_from_this_organizer?: MoreCollectionsFromThisOrganizer,
    other_events_you_may_like?: OtherEventsYouMayLike,
) => {
    const organizerUrl = collection.organizer?.url || '';
    const organizerLocalizedUrl =
        organizerUrl && organizerUrl.startsWith('http')
            ? organizerUrl.replace(
                  new URL(organizerUrl).origin,
                  languageSubdirectory,
              )
            : organizerUrl;
    // BUGBOT (Medium): the legacy code MUTATED each `relative_url` in place
    // (prepending languageSubdirectory during render). `getPropsFrom` runs on
    // every render of `App` (React 19 StrictMode double-invokes; any provider
    // update re-runs it), and the `serverData` object is shared/immutable across
    // renders, so a later render would prepend AGAIN → `/fr_CA/fr_CA/cc/...`.
    // Instead build a NON-MUTATING localized copy (fresh from the pristine
    // source each render → idempotent).
    const localizedMoreCollections = more_collections_from_this_organizer
        ? {
              ...more_collections_from_this_organizer,
              event_groups:
                  more_collections_from_this_organizer.event_groups?.map(
                      (col) => ({
                          ...col,
                          relative_url: `${languageSubdirectory}${col.relative_url}`,
                      }),
                  ),
          }
        : more_collections_from_this_organizer;
    return {
        collectionDetailsProps: {
            collectionName: collection.name,
            collectionSummary: collection.summary,
            organizerName: collection.organizer?.name,
            organizerUrl: organizerLocalizedUrl,
            organizerLogoUrl: collection.organizer?.logo?.url,
            organizerFollowersCount:
                collection.organizer?.follow_status?.num_followers,
        },
        organizerPanelProps: {
            organizerName: collection.organizer?.name,
            organizerDescription: collection.organizer?.description,
            organizerLogoUrl: collection.organizer?.logo?.url,
            organizerUrl: organizerLocalizedUrl,
            organizerId: collection.organizer?.id,
            organizers: [collection.organizer?.id],
            collectionName: collection.name,
            collectionId: collection.id,
            gaCategory: AnalyticsCategory.Listing,
            gaSettings: gaSettings,
            isAuthenticated: user.isAuthenticated || false,
            isGDPRCountry: true,
            isFollowing:
                collection.organizer?.follow_status?.followed_by_you || false,
        },
        moreCollectionsFromThisOrganizerProps: localizedMoreCollections,
        otherEventsYouMayLikeProps: other_events_you_may_like,
        collectionShareProps: {
            collectionId: collection.id,
            collectionUrl: collection.url,
            collectionName: collection.name,
            ...share_props,
        } as ShareCollectionProps,
    };
};

export interface PortedAppProps {
    serverData: ServerData;
    header: GlobalHeaderProps;
    footer: FooterProps;
    locale: string;
}

export const App: React.FC<PortedAppProps> = ({
    serverData,
    header,
    footer,
    locale,
}) => {
    const env = serverData.env as Env;
    const user = serverData.user;
    const featureFlags: FeatureFlags = serverData.featureFlags || {};
    const preview = serverData.preview || false;

    const isMoved =
        'collection_moved' in serverData && serverData.collection_moved;
    const collection = (serverData as CollectionServerData).collection;
    const collectionId = collection?.id;
    const organizerId = collection?.organizer?.id;

    const isOrganizerInListingView =
        (serverData as CollectionServerData).permissions?.can_manage &&
        !preview;

    // BUGBOT (High/Medium, round 15): the language subdirectory is the
    // PARENT LANGUAGE code, not the full underscore locale — see
    // `lib/languageSubdirectory.ts` for the Eventbrite routing contract
    // (`/fr/`, collapse of region variants, no `en_*` prefix).
    const languageSubdirectory: string = languageSubdirectoryFromLocale(
        env?.localeInfo?.locale,
        Boolean(env?.localeInfo?.uses_language_subdirectory),
    );

    const otherEventsYouMayLikeProps =
        (serverData as CollectionMovedServerData)
            .other_events_you_may_like;

    // `getPropsFrom` reads `collection.*` — only valid on the (non-moved)
    // path. The legacy `app.tsx` early-returned before it for
    // `collection_moved`; we scope it to the non-moved branch the same way.
    const inner = isMoved ? (
        <PageLayout preview={preview}>
            <CollectionError
                otherEventsYouMayLikeProps={otherEventsYouMayLikeProps}
            />
        </PageLayout>
    ) : (() => {
        const serverProps = serverData as CollectionServerData;
        const derivedProps = getPropsFrom(
            user,
            serverProps.gaSettings,
            serverProps.collection,
            serverProps.share_props,
            languageSubdirectory,
            serverProps.more_collections_from_this_organizer,
            serverProps.other_events_you_may_like,
        );
        return (
            <PageLayout preview={preview}>
                {isOrganizerInListingView && <OrganizerUiBanner />}
                <Collection
                    env={env}
                    collectionDetailsProps={derivedProps.collectionDetailsProps}
                    organizerPanelProps={derivedProps.organizerPanelProps}
                    collectionShareProps={derivedProps.collectionShareProps}
                    moreCollectionsFromThisOrganizerProps={
                        derivedProps.moreCollectionsFromThisOrganizerProps
                    }
                    otherEventsYouMayLikeProps={
                        derivedProps.otherEventsYouMayLikeProps
                    }
                />
            </PageLayout>
        );
    })();

    // BUGBOT (Medium): preview mode must be CHROMELESS. eb-ui's `PageLayout`
    // IS the chrome (SiteStructure header/footer) and, when `preview`, renders
    // ONLY `PageLayoutPreview` — its own test asserts the Eventbrite
    // header/footer banners are ABSENT in preview. `.cc-preview` is a
    // full-screen `position:fixed` overlay, so wrapping it in the shell left
    // the GlobalHeader + footer stacked above/behind it with `main` collapsed
    // to 0 height (the broken preview the user saw). In preview, skip the
    // shell entirely and reproduce its `<main class="cc-main">` shape so the
    // page styles keep applying.
    const body = (
        <FeatureFlagsProvider value={featureFlags}>
            <ApplicationContext.Provider value={serverData}>
                <AnalyticsContext.Provider
                    value={{
                        collectionId: collectionId || '',
                        organizerId: organizerId || '',
                    }}
                >
                    {inner}
                </AnalyticsContext.Provider>
            </ApplicationContext.Provider>
        </FeatureFlagsProvider>
    );

    if (preview) {
        return <main className="cc-main">{body}</main>;
    }

    return (
        <BspPageShell
            header={header}
            footer={footer}
            mainClassName="cc-main"
        >
            {body}
        </BspPageShell>
    );
};
